import React from "react";
import { skillBeltRows } from "../../mock";

const SectionLabel = ({ index, title }) => (
  <div className="flex items-center gap-3 mb-10">
    <span className="font-mono text-[11px] tracking-[0.22em] text-[#B8542B]">
      {index}
    </span>
    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#6B5F52]">
      {title}
    </span>
    <span className="flex-1 h-px bg-[#E8E0D3]" />
  </div>
);

const SkillTile = ({ name }) => (
  <div className="mx-2 select-none shrink-0">
    <div
      className="px-5 py-3 rounded-2xl backdrop-blur-md whitespace-nowrap"
      style={{
        background:
          "linear-gradient(140deg, rgba(138,90,61,0.32) 0%, rgba(184,84,43,0.22) 55%, rgba(138,90,61,0.28) 100%)",
        border: "1px solid rgba(138,90,61,0.38)",
        boxShadow:
          "0 12px 30px -12px rgba(94,55,30,0.35), inset 0 1px 0 rgba(255,236,214,0.55), inset 0 -8px 18px rgba(94,55,30,0.10)",
      }}
    >
      <span
        className="text-[14px] font-medium tracking-wide"
        style={{ color: "#3a1f12" }}
      >
        {name}
      </span>
    </div>
  </div>
);

const MarqueeRow = ({ items, dir = "left", duration = 60 }) => {
  const doubled = [...items, ...items];
  return (
    <div className="marquee-row overflow-hidden">
      <div
        className={`flex marquee-track ${
          dir === "left" ? "marquee-left" : "marquee-right"
        }`}
        style={{ animationDuration: `${duration}s` }}
      >
        {doubled.map((s, i) => (
          <SkillTile key={`${s}-${i}`} name={s} />
        ))}
      </div>
    </div>
  );
};

const Skills = () => {
  return (
    <section
      id="skills"
      className="relative py-24 md:py-32 bg-[#F5F0E8]/55 overflow-hidden"
    >
      {/* Decorative warm color blobs (visible through glass tiles) */}
      <div
        aria-hidden
        className="absolute -top-32 -left-24 w-[440px] h-[440px] rounded-full bg-[#B8542B]/12 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 right-0 w-[520px] h-[520px] rounded-full bg-[#8a5a3d]/14 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] h-[380px] rounded-full bg-[#d4a373]/18 blur-3xl pointer-events-none"
      />

      <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
        <SectionLabel index="04" title="Skills & toolkit" />

        <div className="grid md:grid-cols-12 gap-8 md:gap-10 mb-14">
          <h2 className="md:col-span-8 font-serif text-3xl md:text-5xl text-[#1F1B16] leading-[1.1] tracking-tight">
            The instruments I reach for, in{" "}
            <span className="italic text-[#8a5a3d]">constant motion</span>.
          </h2>
          <p className="md:col-span-4 text-[#6B5F52] text-base leading-relaxed md:pt-3">
            Tool-fluent, not tool-loyal. Hover the belt to pause it — the right instrument depends on the team, the risk, and the runway.
          </p>
        </div>
      </div>

      {/* Full-width belt of glassmorphic skill tiles */}
      <div className="relative max-w-[100vw]">
        {/* Edge blur overlays — backdrop-blur fading into the page so off-belt tiles look softly out-of-focus */}
        <div
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-28 md:w-56 z-20 pointer-events-none backdrop-blur-md"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 35%, rgba(0,0,0,0) 100%)",
            maskImage:
              "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 35%, rgba(0,0,0,0) 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute right-0 top-0 bottom-0 w-28 md:w-56 z-20 pointer-events-none backdrop-blur-md"
          style={{
            WebkitMaskImage:
              "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 35%, rgba(0,0,0,0) 100%)",
            maskImage:
              "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 35%, rgba(0,0,0,0) 100%)",
          }}
        />

        <div className="space-y-4 py-3">
          <MarqueeRow items={skillBeltRows[0]} dir="left" duration={55} />
          <MarqueeRow items={skillBeltRows[1]} dir="right" duration={75} />
          <MarqueeRow items={skillBeltRows[2]} dir="left" duration={90} />
        </div>
      </div>
    </section>
  );
};

export default Skills;
